import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { ToastService } from '../services/toast.service';
import { environment } from '../../../environments/environment';

/**
 * Auth Interceptor - Adds authorization header and handles auth errors
 *
 * Features:
 * - Adds Bearer token to outgoing requests
 * - Skips auth header for public endpoints
 * - Handles 401 Unauthorized errors
 * - Handles 403 Forbidden errors
 * - Token refresh logic (when refresh endpoint is implemented)
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip auth header for public endpoints
  const publicEndpoints = [
    '/admin/login',
    '/admin/register',
    '/admin/forgot-password',
    '/admin/reset-password',
    '/admin/verify-email',
    '/admin/resend-otp',
  ];

  const isPublicEndpoint = publicEndpoints.some((endpoint) =>
    req.url.includes(endpoint)
  );

  // Clone request to enable withCredentials for cookie support
  let authReq = req.clone({
    withCredentials: true
  });

  // CSRF Protection Flow
  const csrfToken = authService.getCsrfToken();
  const unsafeMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];

  if (csrfToken && unsafeMethods.includes(req.method)) {
    authReq = authReq.clone({
      setHeaders: {
        'x-csrf-token': csrfToken
      }
    });
  }

  // Note: Bearer token injection removed as we've transitioned to secure cookies

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!environment.production) {
        console.error('HTTP Error Status:', error.status);
      }
      if (error.status === 401 && !isPublicEndpoint) {
        if (!environment.production) {
          console.warn('401 Unauthorized - Attempting to handle');
        }
        return handle401Error(authService, error);
      }

      if (error.status === 403) {
        return handle403Error(error, router);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Handle 401 Unauthorized error
 * Attempts to refresh token or redirects to login
 */
function handle401Error(
  authService: AuthService,
  error: HttpErrorResponse
): Observable<HttpEvent<unknown>> {
  if (authService.isAuthenticated()) {
    authService.signout();
    if (!environment.production) {
      console.warn('Unauthorized - Redirecting to login');
    }
  }
  return throwError(() => error);
}

/**
 * Handle 403 Forbidden error
 */
function handle403Error(
  error: HttpErrorResponse,
  router: Router
): Observable<never> {
  router.navigate(['/unauthorized']);
  return throwError(() => error);
}
